/**
 * useParentFieldBindingConfig — konfiguracja rodzaju wiązania `parent_field` („Pole kartoteki:
 * <nazwa>") w edytorze wiązań parametrów słownika, na ekranie TABEL DODATKOWYCH.
 *
 * Wiązanie czyta wartość parametru z pola rekordu-RODZICA wiersza (kartoteki, pod którą żyje
 * tabela dodatkowa). Hook rozstrzyga:
 *   - czy właściciel tabeli to obsługuje — pracownicy, kontrahenci, użytkownicy albo Dokumenty
 *     (mirror `resolveParentFieldKartoteki` w `apps/api/lib/additional/validate-parent-field-bindings.ts`;
 *     firmy, jednostki organizacyjne, kontakty kontrahentów i każdy inny właściciel → brak opcji);
 *   - ile selektorów pola pokazać: Dokumenty (tabela wspólna 7 klas) → po jednym na klasę,
 *     tabela jednej klasy → jeden, pozostali właściciele → jeden (`'default'`);
 *   - jakie pola oferować: katalog pól kartoteki właściciela (to samo źródło i fetcher co kreator
 *     procesów — `fieldsForModuleQueryOptions`; klucz cache dzielimy z kreatorem tylko dla
 *     Dokumentów, bo kreator woła z kodem kwalifikowanym `crm_core.contractors`, a my z gołym
 *     `contractors`), bez pól wielowartościowych (`multiselect`, `reference` z wieloma rekordami
 *     — parametr przyjmuje jedną wartość).
 *
 * Pól wrażliwych (PESEL, numer dowodu, sekret TOTP, `*_encrypted`) front NIE odfiltrowuje —
 * katalog ich nie oznacza, a lista zakazanych slugów ma jedno źródło po stronie serwera
 * (`lib/dictionaries/sensitive-guard.ts`), który odrzuca zapis takiego wiązania.
 *
 * Katalogi pobierane są dopiero gdy `enabled` (edytor wiązań faktycznie jest na ekranie) —
 * dla tabeli wspólnej Dokumentów to 7 zapytań, więc nie odpalamy ich na zapas.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentClass } from '@saas/shared';
import { useQueries } from '@tanstack/react-query';

import type {
  DictionaryBindingSiblingField,
  DictionaryParentFieldConfig,
} from '@/components/dictionaries/DictionaryParamBindingsEditor';
import {
  DOCUMENTS_TARGET_MODULE_CODE,
  fieldsForModuleQueryOptions,
  type ModuleField,
} from '@/components/process-builder/useFieldsForModule';
import type { ParentFieldBindingKey } from '@/hooks/useDictionary';
import { CLASS_KARTOTEKA_SLUGS } from '@/lib/documents/class-kartoteka-slug';

/** Rejestrowy opis właściciela tabeli dodatkowej (podzbiór wiersza z `/settings/additional-tables`). */
export interface AdditionalTableOwnerRef {
  parentTableName: string;
  parentEntityType: 'system' | 'custom';
  parentEntity: string;
}

/** Jeden selektor pola rekordu-rodzica: skąd pobrać katalog i jak go podpisać. */
interface ParentFieldTarget {
  key: ParentFieldBindingKey;
  /** Kod modułu dla `fieldsForModuleQueryOptions` (Dokumenty: wspólny kod + klasa). */
  moduleSlug: string;
  documentClass: DocumentClass | null;
  /** Klucz i18n etykiety selektora (nazwa klasy); `null` = jeden selektor bez etykiety. */
  labelKey: string | null;
}

interface ParentFieldOwner {
  /** Klucz i18n nazwy kartoteki do etykiety rodzaju („Pole kartoteki: …"). */
  ownerLabelKey: string;
  targets: ParentFieldTarget[];
}

/** Właściciele spoza Dokumentów obsługujący `parent_field`: fizyczna tabela rodzica → klucz i18n. */
const NON_DOCUMENT_OWNER_LABEL_KEYS: ReadonlyMap<string, string> = new Map([
  ['employees', 'additionalTables.admin.parentOptions.employees'],
  ['contractors', 'additionalTables.admin.parentOptions.contractors'],
  ['users', 'additionalTables.admin.parentOptions.users'],
]);

/** Nazwa wspólnej tabeli dodatkowej Dokumentów (`parent_entity` — wszystkie 7 klas). */
const SHARED_DOCUMENTS_PARENT_ENTITY = 'documents';

const DOCUMENT_CLASSES = Object.keys(CLASS_KARTOTEKA_SLUGS) as DocumentClass[];

function documentClassLabelKey(cls: DocumentClass): string {
  return `tenant.documentTypes.classes.${cls}`;
}

/**
 * Właściciel tabeli → selektory pola rodzica; `null` = właściciel nie obsługuje `parent_field`
 * (opcja nie pojawia się w edytorze).
 */
export function resolveParentFieldOwner(table: AdditionalTableOwnerRef): ParentFieldOwner | null {
  // Front sprawdza `system` jawnie, serwer nie musi: właściciel typu `custom` (moduł własny) ma
  // własną tabelę rodzica `<code>_module_<slug>`, więc po stronie serwera nigdy nie trafi na
  // employees/contractors/users/documents i nie przejdzie dopasowania.
  if (table.parentEntityType !== 'system') return null;

  if (table.parentTableName === 'documents') {
    if (table.parentEntity === SHARED_DOCUMENTS_PARENT_ENTITY) {
      return {
        ownerLabelKey: 'additionalTables.admin.parentOptions.documents',
        targets: DOCUMENT_CLASSES.map((cls) => ({
          key: cls,
          moduleSlug: DOCUMENTS_TARGET_MODULE_CODE,
          documentClass: cls,
          labelKey: documentClassLabelKey(cls),
        })),
      };
    }
    // Tabela jednej klasy: `parent_entity` = slug kartoteki tej klasy.
    const cls = DOCUMENT_CLASSES.find((c) => CLASS_KARTOTEKA_SLUGS[c] === table.parentEntity);
    if (!cls) return null;
    return {
      ownerLabelKey: documentClassLabelKey(cls),
      targets: [
        {
          key: cls,
          moduleSlug: DOCUMENTS_TARGET_MODULE_CODE,
          documentClass: cls,
          labelKey: null,
        },
      ],
    };
  }

  const ownerLabelKey = NON_DOCUMENT_OWNER_LABEL_KEYS.get(table.parentTableName);
  if (!ownerLabelKey) return null;
  return {
    ownerLabelKey,
    targets: [
      { key: 'default', moduleSlug: table.parentTableName, documentClass: null, labelKey: null },
    ],
  };
}

/** Parametr słownika przyjmuje jedną wartość — pola wielowartościowe nie są kandydatami. */
export function isSingleValueField(field: ModuleField): boolean {
  return (
    field.fieldType !== 'multiselect' &&
    !(field.fieldType === 'reference' && field.relationMultiple === true)
  );
}

/**
 * Konfiguracja opcji „Pole kartoteki" dla edytora wiązań albo `null`, gdy właściciel tabeli jej
 * nie obsługuje. `enabled` — czy edytor jest na ekranie (bramka pobierania katalogów pól).
 */
export function useParentFieldBindingConfig(
  table: AdditionalTableOwnerRef,
  enabled: boolean,
): DictionaryParentFieldConfig | null {
  const { t } = useTranslation();
  const { parentTableName, parentEntityType, parentEntity } = table;
  const owner = useMemo(
    () => resolveParentFieldOwner({ parentTableName, parentEntityType, parentEntity }),
    [parentTableName, parentEntityType, parentEntity],
  );
  const targets = owner?.targets ?? [];

  const results = useQueries({
    queries: targets.map((target) => {
      const base = fieldsForModuleQueryOptions(target.moduleSlug, target.documentClass);
      return { ...base, enabled: enabled && base.enabled };
    }),
  });

  if (!owner) return null;

  return {
    label: t('additionalTables.admin.addField.parentFieldKind', {
      kartoteka: t(owner.ownerLabelKey),
    }),
    keys: targets.map((target, index) => {
      const result = results[index];
      const fields: DictionaryBindingSiblingField[] = (result?.data?.fields ?? [])
        .filter(isSingleValueField)
        .map((f) => ({ slug: f.slug, name: f.name }));
      return {
        key: target.key,
        label: target.labelKey !== null ? t(target.labelKey) : null,
        fields,
        isLoading: result?.isLoading === true,
        loadError: result?.isError === true,
      };
    }),
  };
}
